/**
=========================================================
* Rainman React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

// Rainman React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Rainman React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Services
import AuthService from "services/authService";
import axiosInstance from "utils/axios";

// Images
import profileImage from "assets/images/rainman_profile.png";
import backgroundImage from "assets/images/bg-profile.jpeg";

function Profile() {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [minTradeAmount, setMinTradeAmount] = useState("10.0");
  const [updatingTradeAmount, setUpdatingTradeAmount] = useState(false);
  const [tradeAmountSuccess, setTradeAmountSuccess] = useState(false);
  const [safetyBufferPercentage, setSafetyBufferPercentage] = useState("1.0");


  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = await AuthService.getCurrentUser();
        setUserInfo(user);
        if (user?.min_trade_amount) {
          setMinTradeAmount(user.min_trade_amount.toString());
        }
        if (user?.safety_buffer_percentage) {
          setSafetyBufferPercentage(user.safety_buffer_percentage.toString());
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  const getWebhookUrl = () => {
    // Get the backend base URL from environment or construct it
    let baseUrl = process.env.REACT_APP_BACKEND_BASE_URL;
    
    if (!baseUrl) {
      // Fallback construction for development
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = process.env.REACT_APP_BACKEND_PORT || "8000";
      
      if (hostname === "localhost") {
        baseUrl = `${protocol}//localhost:${port}`;
      } else {
        // For production, assume same hostname different port or subdomain
        baseUrl = `${protocol}//${hostname}:${port}`;
      }
    }
    
    // Remove trailing slash if present
    baseUrl = baseUrl.replace(/\/$/, '');
    
    return `${baseUrl}/api/v1/alerts/webhook/tradingview-secure?api_key=${userInfo?.api_key || "YOUR_API_KEY"}`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
    } catch (err) {
      console.error("Failed to copy: ", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
      } catch (fallbackErr) {
        console.error("Fallback copy failed: ", fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleCloseCopyAlert = () => {
    setCopySuccess(false);
  };

  const handleUpdateTradeConfig = async () => {
    try {
      setUpdatingTradeAmount(true);
      
      const response = await axiosInstance.put('api/v1/users/trade-config', {
        min_trade_amount: parseFloat(minTradeAmount) || 0,
        safety_buffer_percentage: parseFloat(safetyBufferPercentage) || 0
      });

      if (response.status === 200) {
        const updatedUser = response.data;
        setUserInfo(updatedUser);
        setTradeAmountSuccess(true);
        setTimeout(() => {
          setTradeAmountSuccess(false);
        }, 4000);
      } else {
        console.error('Failed to update trade configuration');
      }
    } catch (error) {
        console.error('Error updating trade configuration:', error);
    } finally {
      setUpdatingTradeAmount(false);
    }
  };

  const handleCloseTradeAmountAlert = () => {
    setTradeAmountSuccess(false);
  };



  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={4} textAlign="center">
          <MDTypography variant="h6" fontWeight="medium">
            Loading Profile...
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mb={2} />

        {/* Profile Header */}
        <MDBox position="relative" mb={5}>
          <MDBox
            display="flex"
            alignItems="center"
            position="relative"
            minHeight="18.75rem"
            borderRadius="xl"
            sx={{
              backgroundImage: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                `${linearGradient(
                  rgba(gradients.info.main, 0.6),
                  rgba(gradients.info.state, 0.6)
                )}, url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "50%",
              overflow: "hidden",
            }}
          />
          <Card
            sx={{
              position: "relative",
              mt: -8,
              mx: 3,
              py: 2,
              px: 2,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <MDAvatar src={profileImage} alt="profile-image" size="xl" shadow="sm" />
              </Grid>
              <Grid item>
                <MDBox height="100%" mt={0.5} lineHeight={1}>
                  <MDTypography variant="h5" fontWeight="medium">
                    {userInfo?.username || "User"}
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Trading Account Holder
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          </Card>
        </MDBox>

        {/* Profile Content */}
        <MDBox mt={5} mb={3}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12} md={6} xl={4}>
              <Card sx={{ height: "100%" }}>
                <MDBox pt={3} px={3}>
                  <MDBox display="flex" alignItems="center" mb={2}>
                    <Icon sx={{ mr: 1, color: "primary.main" }}>person</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      Personal Information
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox p={3}>
                  {/* Username */}
                  <MDBox mb={3}>
                    <MDBox display="flex" alignItems="center" mb={1}>
                      <Icon sx={{ mr: 1, fontSize: "1rem", color: "text.secondary" }}>
                        account_circle
                      </Icon>
                      <MDTypography variant="button" fontWeight="medium" color="text">
                        Username
                      </MDTypography>
                    </MDBox>
                    <MDBox pl={3}>
                      <MDTypography variant="body2" color="dark" fontWeight="regular">
                        {userInfo?.username || "Not available"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* Email */}
                  <MDBox mb={3}>
                    <MDBox display="flex" alignItems="center" mb={1}>
                      <Icon sx={{ mr: 1, fontSize: "1rem", color: "text.secondary" }}>
                        email
                      </Icon>
                      <MDTypography variant="button" fontWeight="medium" color="text">
                        Email
                      </MDTypography>
                    </MDBox>
                    <MDBox pl={3}>
                      <MDTypography variant="body2" color="dark" fontWeight="regular">
                        {userInfo?.email || "Not available"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {/* Status */}
                  <MDBox>
                    <MDBox display="flex" alignItems="center" mb={1}>
                      <Icon sx={{ mr: 1, fontSize: "1rem", color: "text.secondary" }}>
                        verified
                      </Icon>
                      <MDTypography variant="button" fontWeight="medium" color="text">
                        Account Status
                      </MDTypography>
                    </MDBox>
                    <MDBox pl={3}>
                      <MDTypography 
                        variant="body2" 
                        color={userInfo?.is_active ? "success" : "error"} 
                        fontWeight="medium"
                      >
                        {userInfo?.is_active ? "Active" : "Inactive"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            {/* API & Integration */}
            <Grid item xs={12} md={6} xl={8}>
              <Card sx={{ height: "100%" }}>
                <MDBox pt={3} px={3}>
                  <MDBox display="flex" alignItems="center" mb={2}>
                    <Icon sx={{ mr: 1, color: "primary.main" }}>integration_instructions</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      API & TradingView Integration
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox p={3}>
                  {/* API Key Section */}
                  <MDBox mb={4}>
                    <MDBox display="flex" alignItems="center" mb={2}>
                      <Icon sx={{ mr: 1, fontSize: "1.2rem", color: "text.secondary" }}>
                        vpn_key
                      </Icon>
                      <MDTypography variant="h6" fontWeight="medium" color="text">
                        API Key
                      </MDTypography>
                      <Tooltip title="Copy API Key">
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(userInfo?.api_key || "")}
                          sx={{ ml: 1 }}
                        >
                          <Icon fontSize="small">content_copy</Icon>
                        </IconButton>
                      </Tooltip>
                    </MDBox>
                    <MDBox pl={3}>
                      <MDTypography 
                        variant="body2" 
                        color="dark" 
                        fontWeight="regular"
                        sx={{ 
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          backgroundColor: "grey.100",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          fontSize: "0.8rem",
                          border: "1px solid #e0e0e0"
                        }}
                      >
                        {userInfo?.api_key || "Not available"}
                      </MDTypography>
                      <MDTypography 
                        variant="caption" 
                        color="text" 
                        fontWeight="regular"
                        sx={{ mt: 1, display: "block" }}
                      >
                        🔐 Keep this API key secure. It provides access to your trading accounts.
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <Divider sx={{ my: 3 }} />

                  {/* TradingView Webhook URL Section */}
                  <MDBox>
                    <MDBox display="flex" alignItems="center" mb={2}>
                      <Icon sx={{ mr: 1, fontSize: "1.2rem", color: "text.secondary" }}>
                        link
                      </Icon>
                      <MDTypography variant="h6" fontWeight="medium" color="text">
                        TradingView Webhook URL
                      </MDTypography>
                      <Tooltip title="Copy Webhook URL">
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(getWebhookUrl())}
                          sx={{ ml: 1 }}
                        >
                          <Icon fontSize="small">content_copy</Icon>
                        </IconButton>
                      </Tooltip>
                    </MDBox>
                    <MDBox pl={3}>
                      <MDTypography 
                        variant="body2" 
                        color="dark" 
                        fontWeight="regular"
                        sx={{ 
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          backgroundColor: "grey.100",
                          padding: "12px",
                          borderRadius: "8px",
                          fontSize: "0.75rem",
                          lineHeight: 1.6,
                          border: "1px solid #e0e0e0"
                        }}
                      >
                        {getWebhookUrl()}
                      </MDTypography>
                      
                      {/* Instructions */}
                      <MDBox 
                        mt={2} 
                        p={2} 
                        sx={{ 
                          backgroundColor: "rgba(33, 150, 243, 0.1)", 
                          borderRadius: "8px",
                          border: "1px solid rgba(33, 150, 243, 0.2)"
                        }}
                      >
                        <MDTypography 
                          variant="button" 
                          color="info" 
                          fontWeight="bold"
                          sx={{ display: "block", mb: 1 }}
                        >
                          📋 TradingView Setup Instructions:
                        </MDTypography>
                        <MDTypography 
                          variant="caption" 
                          color="text" 
                          fontWeight="regular"
                          sx={{ display: "block", mb: 0.5 }}
                        >
                          1. Copy the webhook URL above using the copy button
                        </MDTypography>
                        <MDTypography 
                          variant="caption" 
                          color="text" 
                          fontWeight="regular"
                          sx={{ display: "block", mb: 0.5 }}
                        >
                          2. In TradingView, open any chart and click "Create Alert"
                        </MDTypography>
                        <MDTypography 
                          variant="caption" 
                          color="text" 
                          fontWeight="regular"
                          sx={{ display: "block", mb: 0.5 }}
                        >
                          3. In the alert dialog, find the "Webhook URL" field
                        </MDTypography>
                        <MDTypography 
                          variant="caption" 
                          color="text" 
                          fontWeight="regular"
                          sx={{ display: "block", mb: 0.5 }}
                        >
                          4. Paste the copied URL and configure your alert conditions
                        </MDTypography>
                        <MDTypography 
                          variant="caption" 
                          color="text" 
                          fontWeight="regular"
                          sx={{ display: "block" }}
                        >
                          5. Save the alert - your trades will now execute automatically!
                        </MDTypography>
                      </MDBox>

                      <MDTypography 
                        variant="caption" 
                        color="success" 
                        fontWeight="medium"
                        sx={{ mt: 2, display: "block" }}
                      >
                        ✅ This URL includes your API key for automatic authentication
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* Trading Configuration */}
        <MDBox mt={5} mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={4}>
              <Card sx={{ height: "100%" }}>
                <MDBox pt={3} px={3}>
                  <MDBox display="flex" alignItems="center" mb={2}>
                    <Icon sx={{ mr: 1, color: "primary.main" }}>account_balance_wallet</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      Trading Configuration
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox p={3}>
                  {/* Trade Configuration */}
                  <MDBox mb={4}>
                    <MDBox pl={3}>
                      {/* Minimum Trade Amount */}
                      <MDBox mb={3}>
                        <TextField
                          fullWidth
                          type="text"
                          label="Minimum Trade Amount (USDT)"
                          value={minTradeAmount}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string, numbers, decimal point, and multiple digits after decimal
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setMinTradeAmount(value === '' ? '' : value);
                            }
                          }}
                          inputProps={{ 
                            style: { fontSize: '0.875rem' },
                            inputMode: 'decimal'
                          }}
                          size="small"
                          FormHelperTextProps={{
                            sx: { color: 'white' }
                          }}
                        />
                      </MDBox>
                      
                      {/* Safety Buffer Percentage */}
                      <MDBox mb={3}>
                        <TextField
                          fullWidth
                          type="text"
                          label="Tranfer Safety Buffer Percentage (%)"
                          value={safetyBufferPercentage}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string, numbers, decimal point, and multiple digits after decimal
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setSafetyBufferPercentage(value === '' ? '' : value);
                            }
                          }}
                          inputProps={{ 
                            style: { fontSize: '0.875rem' },
                            inputMode: 'decimal'
                          }}
                          size="small"
                          FormHelperTextProps={{
                            sx: { color: 'white' }
                          }}
                        />
                      </MDBox>
                      
                      <MDBox mb={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleUpdateTradeConfig}
                          disabled={updatingTradeAmount}
                          size="small"
                          startIcon={<Icon>save</Icon>}
                        >
                          {updatingTradeAmount ? "Updating..." : "Update Trade Config"}
                        </Button>
                      </MDBox>

                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        <Footer />
      </DashboardLayout>

      {/* Copy Success Alert */}
      <Snackbar 
        open={copySuccess} 
        autoHideDuration={4000} 
        onClose={handleCloseCopyAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseCopyAlert} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          ✅ Copied to clipboard successfully!
        </Alert>
      </Snackbar>

      {/* Trade Amount Update Success Alert */}
      <Snackbar 
        open={tradeAmountSuccess} 
        autoHideDuration={4000} 
        onClose={handleCloseTradeAmountAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseTradeAmountAlert} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          ✅ Trade configuration updated successfully!
        </Alert>
      </Snackbar>


    </>
  );
}

export default Profile;
